import { currentLocale } from "./i18n"
/** The server answered with an error. `code` is the stable code from the API. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

/** No answer at all: offline, DNS, the server down. Worth retrying. */
export class NetworkError extends Error {}

let apiBase = ""
let token: string | null = null

export function configureApi(base: string) {
  apiBase = base.replace(/\/$/, "")
}

export function setToken(value: string | null) {
  token = value
}

export function wsUrl(path: string): string {
  const origin = apiBase || window.location.origin
  return origin.replace(/^http/, "ws") + path
}

export interface ApiResponse<T> {
  status: number
  data: T
  headers: Headers
}

export async function api<T = unknown>(
  path: string,
  /** `token` calls as another signed-in account than the active one. */
  init: { method?: string; body?: unknown; token?: string } = {},
): Promise<ApiResponse<T>> {
  let res: Response
  const bearer = init.token ?? token
  try {
    res = await fetch(apiBase + path, {
      method: init.method ?? (init.body === undefined ? "GET" : "POST"),
      headers: {
        ...(init.body === undefined ? {} : { "content-type": "application/json" }),
        ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
        // For the person's emails and pushes, and the sign-in code's email
        "x-hub-locale": currentLocale(),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : String(err))
  }
  const data = res.status === 204 ? null : await res.json().catch(() => null)
  if (!res.ok) {
    // h3 errors carry the code in data.code; better-auth errors in code.
    const code = data?.data?.code ?? data?.code ?? `http_${res.status}`
    const message = data?.statusMessage ?? data?.message ?? res.statusText
    throw new ApiError(res.status, code, message)
  }
  return { status: res.status, data: data as T, headers: res.headers }
}

/** An address on Hub's API, e.g. a file's signed link. */
export function apiUrl(path: string): string {
  return (apiBase || window.location.origin) + path
}

/**
 * Sends a file as the request's body (not a form: some proxies refuse multipart), its name in
 * x-hub-filename. XMLHttpRequest, for the upload's progress.
 */
export function uploadFile<T>(path: string, file: Blob, name: string, onProgress?: (fraction: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("POST", apiBase + path)
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream")
    xhr.setRequestHeader("x-hub-filename", encodeURIComponent(name))
    xhr.setRequestHeader("x-hub-locale", currentLocale())
    if (token) xhr.setRequestHeader("authorization", `Bearer ${token}`)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total)
    }
    xhr.onerror = () => reject(new NetworkError("Upload failed"))
    xhr.onload = () => {
      let data: any = null
      try {
        data = JSON.parse(xhr.responseText)
      } catch {
        // No JSON: the status says it
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T)
      else reject(new ApiError(xhr.status, data?.data?.code ?? `http_${xhr.status}`, data?.statusMessage ?? xhr.statusText))
    }
    xhr.send(file)
  })
}

/** 5xx and network failures are temporary; other errors won't change on retry. */
export function isTemporary(err: unknown): boolean {
  return err instanceof NetworkError || (err instanceof ApiError && (err.status >= 500 || err.status === 408 || err.status === 429))
}
